import { type KeyboardEvent, useMemo, useRef, useState } from 'react';
import { MONTHS, addDays, formatDay, fromISODate, monthGrid, sameDay, startOfDay, toISODate } from '../lib/dates';
import { Calendar, ChevronLeft, ChevronRight, X } from './icons';
import { Popover } from './Popover';

export interface DatePickerProps {
  /** ISO date (yyyy-mm-dd) or null. */
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Red styling when the date is in the past (due dates). */
  due?: boolean;
  id?: string;
  'aria-label'?: string;
  size?: 'md' | 'sm';
  /** borderless trigger (property rows in the mission panel) */
  bare?: boolean;
  disabled?: boolean;
}

/** Custom date picker: quick picks, Monday-first month grid, full keyboard (arrows), bottom sheet on phones. */
export function DatePicker({ value, onChange, placeholder = 'Pick a date', due, id, size = 'md', bare, disabled, ...aria }: DatePickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const today = startOfDay();
  const sel = fromISODate(value);
  const [view, setView] = useState(() => new Date((sel ?? today).getFullYear(), (sel ?? today).getMonth(), 1));
  const [focusD, setFocusD] = useState<Date>(sel ?? today);
  const overdue = !!(due && sel && sel < today);

  const quick = useMemo(() => {
    const t = startOfDay();
    const nextMon = addDays(t, (8 - t.getDay()) % 7 || 7);
    const sat = addDays(t, (6 - t.getDay() + 7) % 7);
    return [['Today', t], ['Tomorrow', addDays(t, 1)], ['This weekend', sat], ['Next week', nextMon], ['In 2 weeks', addDays(t, 14)]] as const;
  }, []);

  const set = (d: Date | null) => {
    onChange(d ? toISODate(d) : null);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const moveFocus = (d: Date) => {
    setFocusD(d);
    if (d.getMonth() !== view.getMonth() || d.getFullYear() !== view.getFullYear()) setView(new Date(d.getFullYear(), d.getMonth(), 1));
    requestAnimationFrame(() => gridRef.current?.querySelector<HTMLButtonElement>('[tabindex="0"]')?.focus());
  };

  const onGridKey = (e: KeyboardEvent) => {
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (step === undefined) return;
    e.preventDefault();
    moveFocus(addDays(focusD, step));
  };

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`ui-trigger${size === 'sm' ? ' sm' : ''}${bare ? ' bare' : ''}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={aria['aria-label']}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ color: overdue ? 'var(--danger)' : 'var(--text-3)', display: 'grid' }}><Calendar /></span>
        <span className="ui-val">
          <span className={sel ? undefined : 'ui-ph'} style={overdue ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
            {sel ? formatDay(sel, today) : placeholder}
          </span>
        </span>
        {sel && (
          <span className="ui-clear" role="button" aria-label="Clear date" onClick={(e) => { e.stopPropagation(); onChange(null); }}>
            <X />
          </span>
        )}
      </button>
      <Popover anchorRef={triggerRef} open={open} onClose={() => setOpen(false)} title={aria['aria-label'] ?? placeholder} minWidth={300}>
        <div className="ui-cal">
          <ul className="ui-cal-quick">
            {quick.map(([label, d]) => (
              <li key={label}>
                <button type="button" onClick={() => set(d)}>
                  {label}
                  <small>{d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}</small>
                </button>
              </li>
            ))}
          </ul>
          <div className="ui-cal-main">
            <div className="ui-cal-head">
              <b>{MONTHS[view.getMonth()]} {view.getFullYear()}</b>
              <div className="ui-cal-nav">
                <button type="button" className="ui-icon-btn" aria-label="Previous month" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}><ChevronLeft /></button>
                <button type="button" className="ui-icon-btn" aria-label="Next month" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}><ChevronRight /></button>
              </div>
            </div>
            <div ref={gridRef} className="ui-cal-grid" role="grid" onKeyDown={onGridKey}>
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => <div key={d} className="dow">{d}</div>)}
              {monthGrid(view).map((d) => {
                const cls = [d.getMonth() !== view.getMonth() && 'out', sameDay(d, today) && 'today', sel && sameDay(d, sel) && 'sel'].filter(Boolean).join(' ');
                return (
                  <button key={d.getTime()} type="button" className={cls} tabIndex={sameDay(d, focusD) ? 0 : -1} aria-label={d.toDateString()} aria-pressed={!!sel && sameDay(d, sel)} onClick={() => set(d)}>
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
            <div className="ui-cal-foot">
              <button type="button" className="ui-btn ghost" onClick={() => set(null)}>No date</button>
              <button type="button" className="ui-btn" onClick={() => moveFocus(today)}>Jump to today</button>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
}
